import {
  TravelBookingAdminActionResultSchema,
  TravelBookingAdminCancelQuoteSchema,
  TravelBookingAdminDeleteResultSchema,
  TravelBookingAdminDetailSchema,
  TravelBookingAdminEticketSchema,
  TravelBookingAdminGatewayFeeBackfillSchema,
  TravelBookingAdminListResponseSchema,
  TravelBookingAdminPassportRevealSchema,
  TravelBookingAdminRefundQuoteSchema,
  TravelBookingAdminRefundResultSchema,
  TravelBookingAdminRequoteSchema,
  TravelBookingAdminResendResultSchema,
  TravelBookingAdminSummarySchema,
  TravelBookingReportSchema,
  type TravelBookingAdminActionResult,
  type TravelBookingAdminCancelInput,
  type TravelBookingAdminCancelQuote,
  type TravelBookingAdminDeleteInput,
  type TravelBookingAdminDeleteResult,
  type TravelBookingAdminDetail,
  type TravelBookingAdminEticket,
  type TravelBookingAdminGatewayFeeBackfill,
  type TravelBookingAdminIssueInput,
  type TravelBookingAdminListQuery,
  type TravelBookingAdminListResponse,
  type TravelBookingAdminPassportReveal,
  type TravelBookingAdminRefundInput,
  type TravelBookingAdminRefundQuote,
  type TravelBookingAdminRefundResult,
  type TravelBookingAdminRequote,
  type TravelBookingAdminResendResult,
  type TravelBookingAdminSummary,
  type TravelBookingReport,
  type TravelBookingReportQuery,
} from '@utazzo/shared';
import { isAxiosError } from 'axios';

import { apiClient } from '@/lib/api-client';

/**
 * Website bookings (flight + hotel) admin API surface. Every JSON response is
 * validated with the matching shared Zod schema so a backend change can't
 * silently corrupt the UI's type assumptions.
 */

const BASE = '/admin/travel-bookings';

export const listTravelBookings = async (
  query: TravelBookingAdminListQuery,
): Promise<TravelBookingAdminListResponse> => {
  const { data } = await apiClient.get<unknown>(BASE, { params: query });
  return TravelBookingAdminListResponseSchema.parse(data);
};

export const getTravelBookingsSummary = async (): Promise<TravelBookingAdminSummary> => {
  const { data } = await apiClient.get<unknown>(`${BASE}/summary`);
  return TravelBookingAdminSummarySchema.parse(data);
};

export const getTravelBooking = async (id: string): Promise<TravelBookingAdminDetail> => {
  const { data } = await apiClient.get<unknown>(`${BASE}/${id}`);
  return TravelBookingAdminDetailSchema.parse(data);
};

export const getTravelBookingEticket = async (id: string): Promise<TravelBookingAdminEticket> => {
  const { data } = await apiClient.get<unknown>(`${BASE}/${id}/eticket`);
  return TravelBookingAdminEticketSchema.parse(data);
};

export const backfillGatewayFees = async (): Promise<TravelBookingAdminGatewayFeeBackfill> => {
  const { data } = await apiClient.post<unknown>(`${BASE}/gateway-fees/backfill`, {});
  return TravelBookingAdminGatewayFeeBackfillSchema.parse(data);
};

export const getTravelBookingsReport = async (
  query: TravelBookingReportQuery,
): Promise<TravelBookingReport> => {
  const { data } = await apiClient.get<unknown>(`${BASE}/reports/monthly`, { params: query });
  return TravelBookingReportSchema.parse(data);
};

export const revealTravellerDocuments = async (
  bookingId: string,
  travellerId: string,
): Promise<TravelBookingAdminPassportReveal> => {
  const { data } = await apiClient.get<unknown>(
    `${BASE}/${bookingId}/travellers/${travellerId}/documents`,
  );
  return TravelBookingAdminPassportRevealSchema.parse(data);
};

/** Supplier calls sit behind these; give them room. */
const SUPPLIER_TIMEOUT_MS = 150_000;

export const requoteTravelBooking = async (id: string): Promise<TravelBookingAdminRequote> => {
  const { data } = await apiClient.post<unknown>(
    `${BASE}/${id}/requote`,
    {},
    { timeout: SUPPLIER_TIMEOUT_MS },
  );
  return TravelBookingAdminRequoteSchema.parse(data);
};

export const issueTravelBooking = async (
  id: string,
  input: TravelBookingAdminIssueInput,
): Promise<TravelBookingAdminActionResult> => {
  const { data } = await apiClient.post<unknown>(`${BASE}/${id}/issue`, input, {
    timeout: SUPPLIER_TIMEOUT_MS,
  });
  return TravelBookingAdminActionResultSchema.parse(data);
};

export const getRefundQuote = async (id: string): Promise<TravelBookingAdminRefundQuote> => {
  const { data } = await apiClient.get<unknown>(`${BASE}/${id}/refund-quote`);
  return TravelBookingAdminRefundQuoteSchema.parse(data);
};

/** The gateway sits behind this one. */
export const refundTravelBooking = async (
  id: string,
  input: TravelBookingAdminRefundInput,
): Promise<TravelBookingAdminRefundResult> => {
  const { data } = await apiClient.post<unknown>(`${BASE}/${id}/refund`, input, {
    timeout: 60_000,
  });
  return TravelBookingAdminRefundResultSchema.parse(data);
};

export const getCancelQuote = async (id: string): Promise<TravelBookingAdminCancelQuote> => {
  const { data } = await apiClient.get<unknown>(`${BASE}/${id}/cancel-quote`, {
    timeout: SUPPLIER_TIMEOUT_MS,
  });
  return TravelBookingAdminCancelQuoteSchema.parse(data);
};

export const cancelTravelBooking = async (
  id: string,
  input: TravelBookingAdminCancelInput,
): Promise<TravelBookingAdminActionResult> => {
  const { data } = await apiClient.post<unknown>(`${BASE}/${id}/cancel`, input, {
    timeout: SUPPLIER_TIMEOUT_MS,
  });
  return TravelBookingAdminActionResultSchema.parse(data);
};

/** No supplier behind this one; the default timeout is plenty. */
export const resendTravelBookingConfirmation = async (
  id: string,
): Promise<TravelBookingAdminResendResult> => {
  const { data } = await apiClient.post<unknown>(`${BASE}/${id}/resend-confirmation`, {});
  return TravelBookingAdminResendResultSchema.parse(data);
};

export const deleteDropOffs = async (
  input: TravelBookingAdminDeleteInput,
): Promise<TravelBookingAdminDeleteResult> => {
  const { data } = await apiClient.post<unknown>(`${BASE}/delete`, input);
  return TravelBookingAdminDeleteResultSchema.parse(data);
};

/**
 * The server's typed refusal, when it sent one.
 *
 * `PRICE_CHANGED` and `NOT_AVAILABLE` carry a fresh quote so the dialog can
 * re-render it in place; `ALREADY_HANDLED` / `NOT_ON_HOLD` mean someone (the
 * guest, another admin) got there first.
 */
export interface ApiRefusal {
  code: string;
  message: string;
  quote: TravelBookingAdminRequote | null;
}

export const readRefusal = (error: unknown): ApiRefusal | null => {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data as
    | { code?: unknown; message?: unknown; quote?: unknown; details?: unknown }
    | undefined;
  if (!body || typeof body !== 'object') return null;
  // The API's exception filter hoists only `code` and `message` to the top
  // level; the rest of the thrown object (the fresh `quote`) sits in `details`.
  const details =
    body.details && typeof body.details === 'object'
      ? (body.details as { quote?: unknown })
      : undefined;
  const code = typeof body.code === 'string' ? body.code : null;
  if (!code) return null;
  const message =
    typeof body.message === 'string'
      ? body.message
      : Array.isArray(body.message)
        ? body.message.join(', ')
        : 'The server refused this action.';
  const parsed = TravelBookingAdminRequoteSchema.safeParse(body.quote ?? details?.quote);
  return { code, message, quote: parsed.success ? parsed.data : null };
};
